import { Dispatch, useEffect, useRef } from 'react';
import HeadsetService, { type CallInfo } from 'softphone-vendor-headsets';
import { HeadsetInputReducerAction } from './reducer';
import { useTypedDispatch } from '../../redux/hooks';
import {
    acceptCall,
    addOneToast,
    endCall,
    toggleHoldCall,
    toggleMuteCall
} from '../../redux/slices';

type IncomingCallEvent = {
    event: 'incoming-call';
    payload: {
        call: CallInfo;
        hasOtherActiveCalls: boolean;
    };
};

type OutgoingCallEvent = {
    event: 'outgoing-call';
    payload: {
        call: CallInfo;
    };
};

type AnswerCallEvent = {
    event: 'answer-call';
    payload: {
        conversationId: string;
    };
};

type RejectCallEvent = {
    event: 'reject-call';
    payload: {
        conversationId: string;
    };
};

type MuteEvent = {
    event: 'set-mute';
    payload: {
        value: boolean;
    };
};

type SetHoldEvent = {
    event: 'set-hold';
    payload: {
        conversationId: string;
        value: boolean;
    };
};

type EndCallEvent = {
    event: 'end-call';
    payload: {
        conversationId: string;
        hasOtherActiveCalls?: boolean;
    };
};

type EndAllCalsEvent = {
    event: 'end-all-calls';
};

export type HeadsetStatus = {
    connected: boolean;
    muted: boolean;
    hold: boolean;
    activeCallId: string | null;
};

export type OperationEvent =
    | MuteEvent
    | IncomingCallEvent
    | OutgoingCallEvent
    | AnswerCallEvent
    | RejectCallEvent
    | SetHoldEvent
    | EndCallEvent
    | EndAllCalsEvent;

interface Props {
    reducer: Dispatch<HeadsetInputReducerAction>;
    headsetService: HeadsetService;
    headsetStatusRef: React.MutableRefObject<HeadsetStatus>;
}

export const useConnectedHeadsets = ({ headsetStatusRef, reducer, headsetService }: Props) => {
    const dispatch = useTypedDispatch();

    const connectingToDeviceRef = useRef<HIDDevice>();

    const filters = [
        {
            usage: 0x0005,
            usagePage: 0x000b
        }
    ];

    async function requestDevice() {
        try {
            const [device] = await navigator.hid.requestDevice({ filters });

            if (!device) {
                console.warn('Invalid device selected');
                return;
            }

            connectDevice(device).catch(() => {
                console.warn('Failed to connect to device');
            });
        } catch (e) {
            console.error('Failed getting hid headset');
        }
    }

    async function connectDevice(device: HIDDevice) {
        if (!device.opened) {
            await device.open();
        }
        connectingToDeviceRef.current = device;
        await headsetService.activeMicChange(device.productName);
    }

    async function disconnectCurrentDevice() {
        connectingToDeviceRef.current = undefined;
        await headsetService.activeMicChange('');
    }

    function updateHeadsetStatus(status: Partial<HeadsetStatus>) {
        // eslint-disable-next-line no-param-reassign
        headsetStatusRef.current = {
            ...headsetStatusRef.current,
            ...status
        };
    }

    function handleOperation(operation: OperationEvent) {
        switch (operation.event) {
            case 'outgoing-call':
                headsetService.outgoingCall(operation.payload.call);
                updateHeadsetStatus({
                    activeCallId: operation.payload.call.conversationId
                });
                break;
            case 'incoming-call':
                headsetService.incomingCall(
                    operation.payload.call,
                    operation.payload.hasOtherActiveCalls
                );
                updateHeadsetStatus({
                    activeCallId: operation.payload.call.conversationId
                });
                break;
            case 'answer-call':
                updateHeadsetStatus({
                    activeCallId: operation.payload.conversationId
                });
                headsetService.answerCall(operation.payload.conversationId);
                break;
            case 'set-mute':
                updateHeadsetStatus({ muted: operation.payload.value });
                headsetService.setMute(operation.payload.value);
                break;
            case 'set-hold':
                updateHeadsetStatus({ hold: operation.payload.value });
                headsetService.setHold(operation.payload.conversationId, operation.payload.value);
                break;
            case 'end-all-calls':
                updateHeadsetStatus({
                    activeCallId: null
                });
                headsetService.endAllCalls();
                break;
	        case 'end-call':
                updateHeadsetStatus({
                    activeCallId: null
                });
                headsetService.endCall(
                    operation.payload.conversationId,
                    operation.payload.hasOtherActiveCalls,
                );
				// Some headsets expect you to explicitly reject calls, if it hasn't been answered yet
				headsetService.rejectCall(
					operation.payload.conversationId,
					operation.payload.hasOtherActiveCalls
				)
        }
    }

    useEffect(() => {
        const sub = headsetService.headsetEvents$.subscribe(event => {
            console.log('Headset event', event);
            switch (event.event) {
                case 'deviceHoldStatusChanged':
                    updateHeadsetStatus({ hold: event.payload.holdRequested });
                    dispatch(
                        toggleHoldCall(
                            event.payload.conversationId ||
                                headsetStatusRef.current.activeCallId ||
                                '',
                            event.payload.holdRequested
                        )
                    );
                    break;
                case 'deviceAnsweredCall':
                    dispatch(
                        acceptCall(
                            event.payload.conversationId ||
                                headsetStatusRef.current.activeCallId ||
                                ''
                        )
                    );
                    break;
                case 'deviceConnectionStatusChanged':
                    switch (event.payload) {
                        case 'checking':
                            reducer({
                                type: 'setHeadsetLoading',
                                payload: true
                            });
                            break;
                        case 'notRunning':
                            updateHeadsetStatus({ connected: false, muted: false, hold: false });

                            reducer({
                                type: 'setHeadset'
                            });
                            connectingToDeviceRef.current = undefined;
                            break;
                        case 'noVendor':
                            updateHeadsetStatus({ connected: false, muted: false, hold: false });

                            reducer({
                                type: 'setHeadset',
                                payload: connectingToDeviceRef.current
                            });
                            connectingToDeviceRef.current = undefined;
                            break;
                        case 'running':
							dispatch(addOneToast({
								type: 'success',
								title: 'Headset',
								content: `Successfully connected to ${connectingToDeviceRef.current?.productName || 'device'}`
							}))
                            updateHeadsetStatus({ connected: true, muted: false, hold: false });

                            reducer({
                                type: 'setHeadset',
                                payload: connectingToDeviceRef.current
                            });
                            connectingToDeviceRef.current = undefined;
                            break;
                    }

                    break;
                case 'deviceEndedCall':
                    dispatch(
                        endCall(
                            event.payload.conversationId ||
                                headsetStatusRef.current.activeCallId ||
                                ''
                        )
                    );

                    updateHeadsetStatus({
                        activeCallId: null
                    });

                    break;
                case 'deviceMuteStatusChanged':
                    updateHeadsetStatus({ muted: event.payload.isMuted });

                    if (!event.payload.conversationId && !headsetStatusRef.current.activeCallId)
                        break;

                    dispatch(
                        toggleMuteCall(
                            event.payload.conversationId ||
                                headsetStatusRef.current.activeCallId ||
                                '',
                            event.payload.isMuted
                        )
                    );
                    break;
                case 'deviceRejectedCall':
                    updateHeadsetStatus({
                        activeCallId: null
                    });
                    dispatch(
                        endCall(
                            event.payload.conversationId ||
                                headsetStatusRef.current.activeCallId ||
                                ''
                        )
                    );
                    break;
                case 'implementationChanged':
					if (!event.payload) {
						dispatch(addOneToast({
							type: 'success',
							title: 'Headset',
							content: `Successfully disconnected from device`
						}))
					}
                    break;
                default:
                    console.log('Unhandled headset event', event);
                    break;
                case 'webHidPermissionRequested':
                    requestDevice();
                    break;
            }
        });

        return () => {
            sub.unsubscribe();
        };
    }, []);

    return {
        disconnectCurrentDevice,
        requestDevice,
        handleOperation,
        connectDevice,
        headsetStatus: headsetStatusRef.current
    };
};
